import React, { ComponentProps, FocusEventHandler, ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import {
  Alert,
  AlertTitle,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material';

import useToggleState from '~/hooks/useToggleState';

import getErrorMessage from '~/utils/getErrorMessage';

const TEXT_FIELD_SLOT_PROPS: ComponentProps<typeof TextField>['slotProps'] = { htmlInput: { readOnly: true } };
const COPY_FEEDBACK_DURATION_MS = 3000;
const COPIED_MESSAGE = 'Copied to clipboard';
const COPY_FAILED_MESSAGE = "Couldn't copy. Select the link and copy it manually.";

// The message outlives isOpen so the tooltip keeps its text while it fades out.
type CopyFeedback = { message: string; isOpen: boolean };

type SpoofingLinkState =
  | { status: 'pending' }
  | { status: 'success'; value: string }
  | { status: 'failed'; error: string };

interface Props {
  buttonLabel: string;
  dialogTitle: ReactNode;
  generateSpoofingLink: () => Promise<string>;
  buttonSx?: SxProps<Theme>;
}

function PortalSpoofButton({ buttonLabel, dialogTitle, generateSpoofingLink, buttonSx }: Props) {
  const textFieldRef = useRef<HTMLInputElement>(null);
  const copyFeedbackTimeout = useRef<NodeJS.Timeout | undefined>(undefined);
  // Bumped on every request, dismiss and unmount, so late results can't update the dialog or clipboard.
  const latestRequestId = useRef(0);
  const { isToggled: isModalOpen, toggleOn: openModal, toggleOff: closeModal } = useToggleState(false);
  const [copyFeedback, setCopyFeedback] = useState<CopyFeedback>({ message: COPIED_MESSAGE, isOpen: false });
  const [spoofingLinkState, setSpoofingLinkState] = useState<SpoofingLinkState>({ status: 'pending' });

  const hideCopyFeedback = useCallback(() => {
    clearTimeout(copyFeedbackTimeout.current);
    setCopyFeedback((current) => ({ ...current, isOpen: false }));
  }, []);

  const showCopyFeedback = useCallback(
    (message: string) => {
      clearTimeout(copyFeedbackTimeout.current);
      setCopyFeedback({ message, isOpen: true });
      copyFeedbackTimeout.current = setTimeout(hideCopyFeedback, COPY_FEEDBACK_DURATION_MS);
    },
    [hideCopyFeedback],
  );

  const copyToClipboard = useCallback(
    async (spoofingLink: string) => {
      const requestId = latestRequestId.current;
      let message = COPIED_MESSAGE;
      try {
        await navigator.clipboard.writeText(spoofingLink);
      } catch {
        message = COPY_FAILED_MESSAGE;
      }
      if (requestId === latestRequestId.current) {
        showCopyFeedback(message);
      }
    },
    [showCopyFeedback],
  );

  const loadSpoofingLink = useCallback(async () => {
    latestRequestId.current += 1;
    const requestId = latestRequestId.current;
    setSpoofingLinkState({ status: 'pending' });
    try {
      const spoofingLink = await generateSpoofingLink();
      if (requestId !== latestRequestId.current) {
        return;
      }
      setSpoofingLinkState({ status: 'success', value: spoofingLink });
      // No longer tied to the click, so some browsers (e.g. Safari) refuse it.
      copyToClipboard(spoofingLink);
    } catch (error) {
      if (requestId !== latestRequestId.current) {
        return;
      }
      setSpoofingLinkState({
        status: 'failed',
        error: getErrorMessage(error, 'Failed to generate spoofing link.'),
      });
    }
  }, [copyToClipboard, generateSpoofingLink]);

  const selectTextFieldText = useCallback<FocusEventHandler>(() => {
    textFieldRef.current?.select();
  }, []);

  const copySpoofingLinkToClipboard = useCallback(() => {
    if (spoofingLinkState.status === 'success') {
      copyToClipboard(spoofingLinkState.value);
    }
  }, [copyToClipboard, spoofingLinkState]);

  const handleModalOpen = useCallback(() => {
    hideCopyFeedback();
    loadSpoofingLink();
    openModal();
  }, [hideCopyFeedback, loadSpoofingLink, openModal]);

  const handleModalClose = useCallback(() => {
    latestRequestId.current += 1;
    hideCopyFeedback();
    closeModal();
  }, [closeModal, hideCopyFeedback]);

  useEffect(() => {
    return () => {
      latestRequestId.current += 1;
      clearTimeout(copyFeedbackTimeout.current);
    };
  }, []);

  return (
    <>
      <Button variant="contained" color="success" onClick={handleModalOpen} sx={buttonSx}>
        {buttonLabel}
      </Button>
      <Dialog open={isModalOpen} onClose={handleModalClose}>
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          {spoofingLinkState.status === 'pending' && (
            <Stack direction="column" alignItems="center" justifyContent="center">
              <CircularProgress />
              <Typography variant="overline">Generating link...</Typography>
            </Stack>
          )}

          {spoofingLinkState.status === 'success' && (
            <Stack direction="column" gap={2}>
              <Alert severity="warning">
                <AlertTitle>Caution!</AlertTitle>
                Only use the link in <b>private/incognito</b> browser mode.
              </Alert>
              <TextField
                inputRef={textFieldRef}
                slotProps={TEXT_FIELD_SLOT_PROPS}
                label="Spoofing link"
                value={spoofingLinkState.value}
                multiline
                autoFocus
                onFocus={selectTextFieldText}
              />
            </Stack>
          )}

          {spoofingLinkState.status === 'failed' && (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={loadSpoofingLink}>
                  Retry
                </Button>
              }
            >
              {spoofingLinkState.error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleModalClose}>Dismiss</Button>
          {/* Controlled only: with the listeners on, describeChild adds the message as a native title. */}
          <Tooltip
            title={copyFeedback.message}
            open={copyFeedback.isOpen}
            placement="top"
            describeChild
            disableHoverListener
            disableFocusListener
            disableTouchListener
          >
            <Button
              variant="contained"
              disabled={spoofingLinkState.status !== 'success'}
              onClick={copySpoofingLinkToClipboard}
            >
              Copy to clipboard
            </Button>
          </Tooltip>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default PortalSpoofButton;
